import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Plus, Trash, CheckCircle, Warning, LinkSimple, DownloadSimple } from "@phosphor-icons/react";

const APP_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function ConnectClaudePage() {
  const [tokens, setTokens] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newlyMinted, setNewlyMinted] = useState(null);
  const [name, setName] = useState("Claude Desktop");

  const load = async () => {
    try { const { data } = await api.get("/mcp/tokens"); setTokens(data.tokens || []); }
    catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const mint = async () => {
    if (!name.trim()) { toast.error("Give the token a name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/mcp/tokens", { name: name.trim() });
      setNewlyMinted(data);
      setName("Claude Desktop");
      load();
      toast.success("Token minted — copy it now, we can't show it again.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Mint failed"); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this token? Any Claude client using it will stop working.")) return;
    try { await api.delete(`/mcp/tokens/${id}`); toast.success("Revoked"); load(); }
    catch (e) { toast.error("Revoke failed"); }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const downloadConnector = async () => {
    try {
      const { data } = await api.get("/mcp/download", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "tnai-mcp-connector.zip";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) { toast.error("Download failed — try again in a moment"); }
  };

  const rawToken = newlyMinted?.token || "PASTE_YOUR_MCP_TOKEN_HERE";
  const configJson = JSON.stringify({
    mcpServers: {
      "tnai-crm": {
        command: "/absolute/path/to/mcp_server/.venv/bin/python",
        args: ["/absolute/path/to/mcp_server/server.py"],
        env: {
          SAKSHI_API_URL: APP_URL,
          SAKSHI_MCP_TOKEN: rawToken,
        },
      },
    },
  }, null, 2);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl" data-testid="connect-claude-page">
      <div className="label-micro">Integrations · MCP</div>
      <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">Connect Claude</h1>
      <p className="text-sm text-slate-600 mt-1 max-w-2xl">
        Talk to your CRM from Claude Desktop, Claude Code, or the Claude Web app. Mint a personal token below and paste it into the config — every tool call runs as you.
      </p>

      {/* Step 1: mint */}
      <section className="mt-8 bg-white border border-slate-200 rounded-md p-5" data-testid="mcp-mint-section">
        <div className="flex items-center gap-2 mb-1"><span className="w-6 h-6 rounded-full bg-blue-600 text-white grid place-items-center text-xs font-bold">1</span><h2 className="font-display text-lg font-bold">Mint a personal token</h2></div>
        <p className="text-xs text-slate-500 mb-4">Give it a memorable name (e.g. "MacBook — Claude Desktop"). You can revoke it any time.</p>
        <div className="flex gap-2 flex-wrap">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Token name"
                 data-testid="mcp-token-name"
                 className="flex-1 min-w-[220px] border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          <button onClick={mint} disabled={busy} data-testid="mcp-mint-btn"
                  className="text-sm inline-flex items-center gap-1.5 bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2 transition-colors disabled:opacity-50">
            <Plus size={14}/>{busy ? "Minting…" : "Generate token"}
          </button>
        </div>
        {newlyMinted && (
          <div className="mt-4 border-l-4 border-emerald-500 bg-emerald-50 rounded-sm p-4" data-testid="mcp-token-shown">
            <div className="flex items-center gap-1.5 text-emerald-800 font-semibold text-sm mb-2"><CheckCircle size={14} weight="fill"/>Copy your token now</div>
            <p className="text-xs text-emerald-800 mb-2">This is the only time we'll show the full token. Store it somewhere safe — 1Password, your OS keychain, etc.</p>
            <div className="flex gap-2">
              <code className="flex-1 bg-white border border-emerald-200 rounded-sm px-3 py-2 text-xs font-mono break-all">{newlyMinted.token}</code>
              <button onClick={() => copy(newlyMinted.token, "Token")} data-testid="mcp-copy-token"
                      className="text-xs inline-flex items-center gap-1 bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm px-3 py-2"><Copy size={12}/>Copy</button>
            </div>
          </div>
        )}
      </section>

      {/* Step 2: instructions */}
      <section className="mt-6 bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center gap-2 mb-1"><span className="w-6 h-6 rounded-full bg-blue-600 text-white grid place-items-center text-xs font-bold">2</span><h2 className="font-display text-lg font-bold">Wire it into Claude</h2></div>
        <p className="text-xs text-slate-500 mb-5">Follow the steps below in order. It's a one-time setup — takes about 5 minutes.</p>

        {/* Prerequisites */}
        <div className="mb-6 border border-slate-200 rounded-sm p-4 bg-slate-50">
          <div className="font-semibold text-sm text-slate-900 mb-2">Before you start — you'll need:</div>
          <ul className="text-xs text-slate-700 space-y-1.5">
            <li className="flex gap-2"><span className="text-blue-600 font-bold">•</span><div><b>Claude Desktop</b> installed on your computer. Free download from <a href="https://claude.ai/download" target="_blank" rel="noreferrer" className="text-blue-700 underline">claude.ai/download</a>. Available for Mac and Windows.</div></li>
            <li className="flex gap-2"><span className="text-blue-600 font-bold">•</span><div><b>Python 3.10 or newer</b>. On Mac, open Terminal and type <code className="bg-white border border-slate-200 px-1 rounded">python3 --version</code> — if you see 3.10 or higher, you're set. If not, get it free from <a href="https://python.org/downloads" target="_blank" rel="noreferrer" className="text-blue-700 underline">python.org/downloads</a>.</div></li>
            <li className="flex gap-2"><span className="text-blue-600 font-bold">•</span><div>The token you copied in <b>Step 1</b> above. If you haven't minted one yet, scroll up and click <b>Generate token</b>.</div></li>
          </ul>
        </div>

        <div className="space-y-6">
          {/* Sub-step A: Download */}
          <div className="border-l-2 border-blue-200 pl-4">
            <div className="text-sm font-bold text-slate-900 mb-1">A. Download the connector to your computer</div>
            <p className="text-xs text-slate-600 mb-3">Click the button below to grab a small ZIP file (~15 KB). It contains the "connector" — a tiny Python program that shuttles messages between Claude and this app. No coding needed on your side.</p>
            <button onClick={downloadConnector} data-testid="mcp-download-btn"
                    className="text-sm inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-4 py-2 transition-colors font-semibold">
              <DownloadSimple size={14} weight="bold"/>Download connector (ZIP)
            </button>
            <div className="text-xs text-slate-600 mt-3 space-y-1.5">
              <div>Once downloaded, <b>unzip</b> the file and move the resulting <code className="bg-slate-100 px-1">mcp_server</code> folder somewhere you'll remember:</div>
              <div className="pl-3"><b>Mac:</b> a good spot is <code className="bg-slate-100 px-1">~/Documents/TNAI Connector/mcp_server</code></div>
              <div className="pl-3"><b>Windows:</b> a good spot is <code className="bg-slate-100 px-1">C:\TNAI Connector\mcp_server</code></div>
              <div>Note the full path — you'll paste it in step D. On Mac, right-click the folder → <b>Get Info</b>; on Windows, right-click → <b>Properties</b>.</div>
            </div>
          </div>

          {/* Sub-step B: Install */}
          <div className="border-l-2 border-blue-200 pl-4">
            <div className="text-sm font-bold text-slate-900 mb-1">B. Install the connector (one-time)</div>
            <p className="text-xs text-slate-600 mb-2">Open <b>Terminal</b> (Mac: Applications → Utilities → Terminal · Windows: Start menu → search "PowerShell") and paste these three lines one by one, hitting <b>Enter</b> after each. Replace <code className="bg-slate-100 px-1">/path/to/mcp_server</code> with the folder path from step A.</p>
            <pre className="bg-slate-900 text-slate-100 rounded-sm px-3 py-2 text-[11px] overflow-x-auto"><code>{`cd /path/to/mcp_server
python3 -m venv .venv
.venv/bin/pip install -r requirements.txt`}</code></pre>
            <p className="text-xs text-slate-500 italic mt-2">You'll see a bunch of "Downloading…" and "Installing…" lines. When Terminal returns to a normal prompt, it's done.</p>
          </div>

          {/* Sub-step C: Locate config */}
          <div className="border-l-2 border-blue-200 pl-4">
            <div className="text-sm font-bold text-slate-900 mb-1">C. Open Claude Desktop's settings file</div>
            <p className="text-xs text-slate-600 mb-2">Open Claude Desktop → click the menu bar → <b>Settings</b> → <b>Developer</b> → <b>Edit Config</b>. This opens a file called <code className="bg-slate-100 px-1">claude_desktop_config.json</code> in your text editor. If the file is empty, just paste the whole JSON block below. If it already has content, we'll merge in the next step.</p>
            <div className="text-[11px] text-slate-500">
              <div>File location (in case you want to find it manually):</div>
              <div className="mt-1"><b>Mac:</b> <code className="bg-slate-100 px-1">~/Library/Application Support/Claude/claude_desktop_config.json</code></div>
              <div><b>Windows:</b> <code className="bg-slate-100 px-1">%APPDATA%\Claude\claude_desktop_config.json</code></div>
            </div>
          </div>

          {/* Sub-step D: Paste config */}
          <div className="border-l-2 border-blue-200 pl-4">
            <div className="text-sm font-bold text-slate-900 mb-1">D. Paste this configuration</div>
            <p className="text-xs text-slate-600 mb-2">Copy the block below and paste it into the config file. Then <b>replace the two paths</b> that read <code className="bg-slate-100 px-1">/absolute/path/to/mcp_server</code> with the real path to the folder from step A.</p>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-slate-700">Paste this into claude_desktop_config.json</div>
              <button onClick={() => copy(configJson, "Config")} data-testid="mcp-copy-config"
                      className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2.5 py-1 hover:bg-slate-900 hover:text-white transition-colors"><Copy size={11}/>Copy JSON</button>
            </div>
            <pre className="bg-slate-900 text-slate-100 rounded-sm px-4 py-3 text-[11px] overflow-x-auto"><code>{configJson}</code></pre>
            <p className="text-xs text-slate-500 italic mt-2">Your token from step 1 is already filled in — no need to touch it. Save the file (<b>Cmd + S</b> on Mac, <b>Ctrl + S</b> on Windows).</p>
            {!newlyMinted && (
              <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                Heads up — you haven't minted a token yet. Go up to <b>Step 1</b> and click <b>Generate token</b> first, then come back here.
              </div>
            )}
          </div>

          {/* Sub-step E: Restart */}
          <div className="border-l-2 border-blue-200 pl-4">
            <div className="text-sm font-bold text-slate-900 mb-1">E. Restart Claude Desktop</div>
            <p className="text-xs text-slate-600">Quit Claude Desktop completely (Mac: <b>Cmd + Q</b> · Windows: right-click the Claude icon in the system tray → <b>Quit</b>) and reopen it. Claude reads the config on startup, so a full quit + relaunch is required — closing the window is not enough.</p>
          </div>

          {/* Sub-step F: Verify */}
          <div className="border-l-2 border-emerald-400 pl-4">
            <div className="text-sm font-bold text-slate-900 mb-1 flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-emerald-600"/>F. Verify it worked</div>
            <p className="text-xs text-slate-600 mb-2">Open a new chat in Claude Desktop and click the small <b>plug icon</b> (🧩 / 🔌) near the message box. You should see <b>tnai-crm</b> listed with ~22 tools inside (list_deals, create_deal, add_deal_note, list_proposals, and so on).</p>
            <p className="text-xs text-slate-600 mb-2">To test, type this into Claude: <code className="bg-slate-100 px-1">"List the top 5 deals in the TNAI Sales Lifecycle pipeline"</code>. Claude should call the tool, show a permission popup the first time (click <b>Allow</b>), and reply with real deals from this app.</p>
            <p className="text-xs text-slate-500 italic">If nothing shows up, double-check the two paths in the config file — the most common issue is a typo or the wrong folder location.</p>
          </div>
        </div>

        {/* Fallback: Claude Code */}
        <div className="mt-6 border border-slate-200 rounded-sm p-4 bg-slate-50">
          <div className="font-semibold text-sm text-slate-900 mb-1">Using Claude Code instead? (developers)</div>
          <p className="text-xs text-slate-600 mb-2">If you use the terminal-based Claude Code CLI, skip everything above and just run this one command (paste your token in place of the placeholder):</p>
          <pre data-testid="mcp-cc-cmd" className="bg-slate-900 text-slate-100 rounded-sm px-3 py-2 text-[11px] overflow-x-auto"><code>{`claude mcp add tnai-crm \\
  --env SAKSHI_API_URL=${APP_URL} \\
  --env SAKSHI_MCP_TOKEN=${rawToken} \\
  -- /abs/path/mcp_server/.venv/bin/python /abs/path/mcp_server/server.py`}</code></pre>
        </div>

        <div className="mt-4 border-l-4 border-amber-500 bg-amber-50 rounded-sm p-3 text-xs text-amber-800 flex items-start gap-1.5">
          <Warning size={14} weight="fill" className="mt-0.5 shrink-0"/>
          <div><b>Stuck?</b> Ping <b>#tnai-suite-help</b> on Slack — most setups take under 5 minutes and someone can screen-share you through it. Every tool call is logged and runs as you, so you can safely revoke your token later (Step 3) if anything ever looks off.</div>
        </div>
      </section>

      {/* Step 3: existing tokens */}
      <section className="mt-6 bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center gap-2 mb-1"><span className="w-6 h-6 rounded-full bg-blue-600 text-white grid place-items-center text-xs font-bold">3</span><h2 className="font-display text-lg font-bold">Your active tokens</h2></div>
        {tokens.length === 0 ? (
          <div className="text-xs text-slate-500 italic mt-2">No tokens yet. Mint one above to get started.</div>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100" data-testid="mcp-token-list">
            {tokens.map(t => (
              <li key={t.id} className="py-3 flex items-center gap-3">
                <LinkSimple size={16} className="text-slate-400 shrink-0"/>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{t.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{t.prefix}… · created {new Date(t.created_at).toLocaleDateString()}{t.last_used_at && ` · last used ${new Date(t.last_used_at).toLocaleDateString()}`}</div>
                </div>
                <button onClick={() => revoke(t.id)} data-testid={`mcp-revoke-${t.id}`}
                        className="text-xs inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-2.5 py-1.5 hover:bg-red-600 hover:text-white transition-colors"><Trash size={12}/>Revoke</button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
